import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Star, Trash2, Loader2 } from "lucide-react";

interface SumoImageData {
  id: string;
  image_url: string;
  alt_text?: string;
  is_primary: boolean;
  display_order: number;
  created_at: string;
}

interface SumoImageGalleryProps {
  sumoId: string;
  onImageDeleted?: () => void;
}

export const SumoImageGallery = ({ sumoId, onImageDeleted }: SumoImageGalleryProps) => {
  const [images, setImages] = useState<SumoImageData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingImages, setDeletingImages] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  const loadImages = async () => {
    if (!sumoId) {
      setImages([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('sumo_images')
        .select('*')
        .eq('sumo_id', sumoId)
        .order('is_primary', { ascending: false })
        .order('display_order', { ascending: true });

      if (error) throw error;

      setImages(data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des images:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les images",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadImages();
  }, [sumoId]);

  const handleSetPrimary = async (imageId: string) => {
    try {
      // Reset all images to not primary
      await supabase
        .from('sumo_images')
        .update({ is_primary: false })
        .eq('sumo_id', sumoId);

      // Set the selected image as primary
      const { error } = await supabase
        .from('sumo_images')
        .update({ is_primary: true })
        .eq('id', imageId);

      if (error) throw error;

      // Update local state
      setImages(prev => prev.map(img => ({
        ...img,
        is_primary: img.id === imageId
      })));

      toast({
        title: "Succès",
        description: "Image principale mise à jour",
      });

    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      toast({
        title: "Erreur",
        description: "Impossible de définir l'image principale",
        variant: "destructive",
      });
    }
  };

  const handleDeleteImage = async (imageId: string, imageUrl: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    setDeletingImages(prev => new Set(prev).add(imageId));

    try {
      // Delete from database
      const { error: dbError } = await supabase
        .from('sumo_images')
        .delete()
        .eq('id', imageId);

      if (dbError) throw dbError;

      // Try to delete from storage
      try {
        const urlParts = imageUrl.split('/');
        const fileName = urlParts[urlParts.length - 1];
        const sumoFolder = urlParts[urlParts.length - 2];
        const filePath = `${sumoFolder}/${fileName}`;
        
        await supabase.storage
          .from('sumo-images')
          .remove([filePath]);
      } catch (storageError) {
        console.warn('Erreur lors de la suppression du fichier de stockage:', storageError);
      }

      // Update local state
      setImages(prev => prev.filter(img => img.id !== imageId));
      
      toast({
        title: "Succès",
        description: "Image supprimée avec succès",
      });

      onImageDeleted?.();

    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'image",
        variant: "destructive",
      });
    } finally {
      setDeletingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Chargement des images...</span>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Aucune image disponible pour ce sumo.</p>
        <p className="text-xs text-muted-foreground mt-2">
          Utilisez l'onglet "Upload de nouvelles images" ci-dessus pour ajouter des images.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {images.map((image) => (
        <Card key={image.id} className="overflow-hidden">
          <div className="relative">
            <OptimizedImage
              src={image.image_url}
              alt={image.alt_text || 'Image du sumo'}
              className="w-full h-48 object-cover"
              width={300}
              height={192}
            />
            {image.is_primary && (
              <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground">
                <Star className="w-3 h-3 mr-1" />
                Principal
              </Badge>
            )}
          </div>
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground truncate">
                {image.alt_text || 'Image du sumo'}
              </span>
              <div className="flex gap-2">
                {!image.is_primary && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetPrimary(image.id)}
                  >
                    <Star className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteImage(image.id, image.image_url)}
                  disabled={deletingImages.has(image.id)}
                >
                  {deletingImages.has(image.id) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};